import React from "react";
import {
    SovosBox,
    SovosButton,
    SovosDialog,
    SovosDialogActions,
    SovosDialogContent,
    SovosDialogTitle,
    SovosIconButton,
    SovosTypography,
} from "mosaic-react";
import { Close, Warning } from "mosaic-react-icons";
import { toSourceColumnLabel } from "../../mappingDisplayLabels";

const WarningSkipDialog = ({ open, onCancel, onConfirm, skippedColumns, warningRows, tableColumns, i18n }) => {
    const hasSkipped = skippedColumns.length > 0;
    const hasWarnings = warningRows.length > 0;

    return (
        <SovosDialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <SovosBox sx={{ position: "absolute", top: 8, right: 8 }}>
                <SovosIconButton size="small" tooltipText={i18n`Close`} onClick={onCancel}>
                    <Close fontSize="small" />
                </SovosIconButton>
            </SovosBox>
            <SovosDialogTitle>{i18n`Some data will be skipped`}</SovosDialogTitle>
            <SovosDialogContent>
                <SovosBox
                    sx={{
                        border: "1px dashed",
                        borderColor: "primary.light",
                        borderRadius: 1,
                        p: 2,
                        mb: 2,
                    }}
                >
                    {hasSkipped && (
                        <SovosBox sx={{ mb: hasWarnings ? 2 : 0 }}>
                            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                <Warning sx={{ color: "warning.main", fontSize: 18 }} />
                                <SovosTypography variant="body2" color="text.secondary">
                                    {i18n`The following columns will not be imported`}
                                </SovosTypography>
                            </SovosBox>
                            {skippedColumns.map((col) => (
                                <SovosTypography key={col} variant="h6" fontWeight={600} sx={{ ml: 3.5 }}>
                                    {toSourceColumnLabel(col)}
                                </SovosTypography>
                            ))}
                        </SovosBox>
                    )}

                    {hasWarnings && (
                        <SovosBox>
                            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                <Warning sx={{ color: "warning.main", fontSize: 18 }} />
                                <SovosTypography variant="body2" color="text.secondary">
                                    {i18n`The following rows still have warnings and will be skipped`}
                                </SovosTypography>
                            </SovosBox>
                            <SovosBox sx={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "inherit" }}>
                                    <thead>
                                        <tr>
                                            {tableColumns.map((col) => (
                                                <th
                                                    key={col}
                                                    style={{
                                                        textAlign: "left",
                                                        padding: "4px 12px",
                                                        color: "rgba(0,0,0,0.6)",
                                                        fontWeight: 400,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {toSourceColumnLabel(col)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {warningRows.map(({ rowId, data, warnings }) => (
                                            <tr key={rowId}>
                                                {tableColumns.map((col) => (
                                                    <td
                                                        key={col}
                                                        style={{
                                                            padding: "4px 12px",
                                                            backgroundColor: warnings[col]
                                                                ? "rgba(211, 47, 47, 0.08)"
                                                                : "transparent",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {data?.[col] ?? ""}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </SovosBox>
                        </SovosBox>
                    )}
                </SovosBox>

                <SovosBox
                    sx={{
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1.5,
                    }}
                >
                    <SovosTypography variant="body2" color="text.secondary">
                        {i18n`The skipped data will not be imported into the framework. Are you sure you want to continue?`}
                    </SovosTypography>
                </SovosBox>
            </SovosDialogContent>
            <SovosDialogActions>
                <SovosButton variant="text" onClick={onCancel}>
                    {i18n`Cancel`}
                </SovosButton>
                <SovosButton variant="contained" onClick={onConfirm}>
                    {i18n`Skip and proceed`}
                </SovosButton>
            </SovosDialogActions>
        </SovosDialog>
    );
};

export default WarningSkipDialog;
